import { WSText } from "@wingspanhq/fe-component-library";
import React from "react";

// Matches bare http(s) URLs in note text.
const URL_PATTERN = /\bhttps?:\/\/[^\s]+/g;
// Trailing characters that are almost always sentence punctuation, not URL.
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

/**
 * Renders a note message, turning any embedded http(s) URL into an inline link.
 * Returns the original string unchanged when no URL is present, so plain notes
 * render exactly as before. Backend notes have no dedicated link field
 * (`IFormNote` is `{ level, message, scope? }`), so links are best-effort from
 * the message text.
 */
export const linkifyText = (message: string): React.ReactNode => {
  if (!URL_PATTERN.test(message)) return message;
  // `test` advances lastIndex on a global regex — reset before reusing it.
  URL_PATTERN.lastIndex = 0;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = URL_PATTERN.exec(message)) !== null) {
    const raw = match[0];
    const trailing = TRAILING_PUNCTUATION.exec(raw)?.[0] ?? "";
    const url = trailing ? raw.slice(0, raw.length - trailing.length) : raw;

    if (match.index > lastIndex) {
      nodes.push(message.slice(lastIndex, match.index));
    }
    nodes.push(
      // Native anchor — no component-library component carries an `href`. The
      // inner WSText applies the link color without any inline style.
      <a key={match.index} href={url} target="_blank" rel="noopener noreferrer">
        <WSText.ParagraphSm inline color="blue500">
          {url}
        </WSText.ParagraphSm>
      </a>
    );
    if (trailing) nodes.push(trailing);
    lastIndex = match.index + raw.length;
  }

  if (lastIndex < message.length) nodes.push(message.slice(lastIndex));
  return nodes;
};
